import React from 'react';
import { Link } from 'react-router-dom';
import KeystoneLogo from '../components/KeystoneLogo';

const COLORS = {
  navy: '#0A2540',
  navyDeep: '#061A2E',
  magenta: '#E91E63',
  gold: '#C9A961',
  cream: '#FAF8F5',
  ink: '#1A2332',
  slate: '#4A5568',
  muted: '#718096',
  border: '#E2E8F0',
  bgSoft: '#F7FAFC',
};

const ASSETS_PATH = '/brochure-assets/logos';

const variants = [
  {
    section: 'Full Lockup · Light Background',
    items: [
      { label: 'Horizontal, with tagline', file: 'keystone-horizontal-light-2048.png', svg: null, bg: '#FFFFFF' },
      { label: 'Horizontal, no tagline', file: 'keystone-horizontal-light-notag-2048.png', svg: null, bg: '#FFFFFF' },
      { label: 'Stacked, with tagline', file: 'keystone-stacked-light-2048.png', svg: null, bg: '#FFFFFF' },
    ],
  },
  {
    section: 'Full Lockup · Dark Background',
    items: [
      { label: 'Horizontal, with tagline', file: 'keystone-horizontal-dark-2048.png', svg: null, bg: COLORS.navy },
      { label: 'Horizontal, no tagline', file: 'keystone-horizontal-dark-notag-2048.png', svg: null, bg: COLORS.navy },
      { label: 'Stacked, with tagline', file: 'keystone-stacked-dark-2048.png', svg: null, bg: COLORS.navy },
    ],
  },
  {
    section: 'Mark Only · App Icon / Favicon',
    items: [
      { label: 'Mark, Primary (navy + magenta + gold)', file: 'keystone-mark-2048.png', svg: 'keystone-mark.svg', bg: '#F1F5F9' },
      { label: 'Mark, White card', file: 'keystone-mark-white-2048.png', svg: 'keystone-mark-white.svg', bg: '#F1F5F9' },
      { label: 'Mark, Mono Navy', file: 'keystone-mark-mono-navy-2048.png', svg: 'keystone-mark-mono-navy.svg', bg: '#F1F5F9' },
      { label: 'Mark, Mono White (reverse)', file: 'keystone-mark-mono-white-2048.png', svg: 'keystone-mark-mono-white.svg', bg: COLORS.navy },
    ],
  },
];

function DownloadCard({ label, file, svg, bg }) {
  const baseName = file.replace('-2048.png', '');
  return (
    <div style={{
      border: `1px solid ${COLORS.border}`,
      borderRadius: 12,
      overflow: 'hidden',
      background: '#fff',
      boxShadow: '0 1px 3px rgba(10, 37, 64, 0.04)',
      transition: 'box-shadow 0.2s, transform 0.2s',
    }}>
      <div style={{
        background: bg,
        padding: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 200,
      }}>
        <img
          src={`${ASSETS_PATH}/${file}`}
          alt={label}
          style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain', display: 'block' }}
        />
      </div>
      <div style={{ padding: '14px 18px', borderTop: `1px solid ${COLORS.border}` }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy, marginBottom: 8 }}>{label}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <a
            href={`${ASSETS_PATH}/${file}`}
            download
            style={{
              padding: '6px 12px',
              background: COLORS.navy,
              color: '#fff',
              borderRadius: 5,
              fontSize: 11,
              fontWeight: 700,
              textDecoration: 'none',
              letterSpacing: '0.04em',
            }}
          >
            ⬇ PNG · 2048px
          </a>
          {svg && (
            <a
              href={`${ASSETS_PATH}/${svg}`}
              download
              style={{
                padding: '6px 12px',
                background: COLORS.magenta,
                color: '#fff',
                borderRadius: 5,
                fontSize: 11,
                fontWeight: 700,
                textDecoration: 'none',
                letterSpacing: '0.04em',
              }}
            >
              ⬇ SVG · vector
            </a>
          )}
          {!svg && baseName.includes('mark') && ['256', '512', '1024'].map(s => (
            <a
              key={s}
              href={`${ASSETS_PATH}/${baseName.replace('-light', '').replace('-dark', '')}-${s}.png`}
              download
              style={{
                padding: '6px 10px',
                background: '#fff',
                color: COLORS.navy,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 5,
                fontSize: 10,
                fontWeight: 700,
                textDecoration: 'none',
                letterSpacing: '0.04em',
              }}
            >
              {s}px
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function BrandAssets() {
  return (
    <div style={{
      minHeight: '100vh',
      background: COLORS.bgSoft,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      color: COLORS.ink,
    }}>
      {/* Top bar */}
      <header style={{
        background: '#fff',
        borderBottom: `1px solid ${COLORS.border}`,
        padding: '16px 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <KeystoneLogo height={36} variant="light" showTagline={false} />
          <div style={{ borderLeft: `1px solid ${COLORS.border}`, paddingLeft: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.navy }}>Brand Assets</div>
            <div style={{ fontSize: 11, color: COLORS.muted }}>Logo system · Hi-res downloads</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Link
            to="/brochure/sales"
            style={{
              padding: '8px 16px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              color: COLORS.navy,
              textDecoration: 'none',
              background: '#fff',
            }}
          >
            ← Back to Brochure
          </Link>
          <a
            href="/brochure-assets/keystone-brand-pack.zip"
            download="Keystone-Brand-Pack.zip"
            style={{
              padding: '10px 20px',
              background: COLORS.magenta,
              color: '#fff',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              textDecoration: 'none',
              letterSpacing: '0.02em',
              boxShadow: '0 4px 12px rgba(233, 30, 99, 0.3)',
            }}
          >
            ⬇ Download Full Brand Pack (.zip)
          </a>
        </div>
      </header>

      <main style={{ maxWidth: 1280, margin: '0 auto', padding: '48px 32px' }}>
        {/* Hero */}
        <div style={{ marginBottom: 48, textAlign: 'left' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
            fontWeight: 700, color: COLORS.magenta, marginBottom: 12,
          }}>
            <span style={{ width: 24, height: 2, background: COLORS.magenta }} />
            Keystone by CloudVoro
          </div>
          <h1 style={{
            fontSize: 48, fontWeight: 900, color: COLORS.navy,
            letterSpacing: '-0.02em', margin: '0 0 12px 0', lineHeight: 1.05,
          }}>
            Brand assets &<br />
            <span style={{ color: COLORS.magenta }}>logo system.</span>
          </h1>
          <p style={{
            fontSize: 17, color: COLORS.slate, lineHeight: 1.55, maxWidth: 720, margin: 0,
          }}>
            Download the full logo system in vector (SVG) and high-resolution raster (PNG up to 2048px) formats. Use SVG for digital wherever possible, it scales perfectly without quality loss. PNG raster is provided for environments that don't support vector.
          </p>
        </div>

        {/* Brand colours */}
        <section style={{
          background: '#fff',
          border: `1px solid ${COLORS.border}`,
          borderRadius: 12,
          padding: 32,
          marginBottom: 48,
        }}>
          <div style={{ fontSize: 11, color: COLORS.magenta, letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 16 }}>Brand Palette</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
            {[
              { name: 'Navy', hex: '#0A2540', label: 'Primary' },
              { name: 'Magenta', hex: '#E91E63', label: 'Accent' },
              { name: 'Gold', hex: '#C9A961', label: 'Premium' },
              { name: 'Cream', hex: '#FAF8F5', label: 'Soft BG' },
              { name: 'Slate', hex: '#4A5568', label: 'Body text' },
            ].map(c => (
              <div key={c.hex} style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${COLORS.border}` }}>
                <div style={{ background: c.hex, height: 80 }} />
                <div style={{ padding: 10, background: '#fff' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy }}>{c.name}</div>
                  <div style={{ fontSize: 10, color: COLORS.muted, fontFamily: 'monospace', marginTop: 2 }}>{c.hex}</div>
                  <div style={{ fontSize: 10, color: COLORS.slate, marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{c.label}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Sales Documents */}
        <section style={{ marginBottom: 48 }}>
          <h2 style={{
            fontSize: 11, color: COLORS.magenta, letterSpacing: '0.15em',
            textTransform: 'uppercase', fontWeight: 700, marginBottom: 16,
          }}>
            Sales Documents · Print-ready
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
            <Link to="/brochure/sales" style={{
              display: 'block', textDecoration: 'none',
              border: `1px solid ${COLORS.border}`, borderRadius: 12,
              overflow: 'hidden', background: '#fff',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 14px 32px rgba(10,37,64,0.10)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{
                aspectRatio: '210/148',
                background: `linear-gradient(135deg, ${COLORS.navy} 0%, #0F3A5F 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontFamily: "'Playfair Display', Georgia, serif", fontSize: 28, fontWeight: 700,
                letterSpacing: '-0.02em', position: 'relative', overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', inset: 0, backgroundImage: `radial-gradient(circle at 20% 30%, ${COLORS.gold}22, transparent 50%)` }} />
                <div style={{ position: 'relative', textAlign: 'center', padding: 24 }}>
                  <div style={{ fontSize: 11, color: COLORS.gold, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 12 }}>Brochure · 8 pages</div>
                  Keystone<br/><em style={{ color: COLORS.gold, fontStyle: 'italic', fontWeight: 500 }}>Production Operating System</em>
                </div>
              </div>
              <div style={{ padding: 20 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.navy }}>Product Brochure</div>
                <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 4, lineHeight: 1.5 }}>
                  8-page A4 sales brochure. Open and use your browser's <strong>Print → Save as PDF</strong>.
                </div>
                <div style={{ marginTop: 12, fontSize: 12, fontWeight: 700, color: COLORS.magenta, letterSpacing: '0.04em' }}>
                  Open brochure →
                </div>
              </div>
            </Link>

            <Link to="/founding-customer" style={{
              display: 'block', textDecoration: 'none',
              border: `1px solid ${COLORS.border}`, borderRadius: 12,
              overflow: 'hidden', background: '#fff',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 14px 32px rgba(10,37,64,0.10)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{
                aspectRatio: '210/148',
                background: `linear-gradient(135deg, ${COLORS.cream} 0%, #F0EBE0 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: COLORS.navy, fontFamily: "'Playfair Display', Georgia, serif", fontSize: 24, fontWeight: 700,
                letterSpacing: '-0.02em', position: 'relative', overflow: 'hidden',
                borderBottom: `4px solid ${COLORS.magenta}`,
              }}>
                <div style={{ position: 'absolute', top: 16, left: 16, right: 16, fontSize: 10, color: COLORS.magenta, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700 }}>
                  Confidential · 5 spots only
                </div>
                <div style={{ textAlign: 'center', padding: 24 }}>
                  Founding Customer<br/><em style={{ color: COLORS.magenta, fontStyle: 'italic', fontWeight: 500 }}>Programme</em>
                </div>
                <div style={{ position: 'absolute', bottom: 12, right: 16, fontSize: 28, fontWeight: 800, color: COLORS.gold, opacity: 0.4, fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: '-0.04em' }}>
                  3-year stair-step
                </div>
              </div>
              <div style={{ padding: 20 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.navy }}>Founding Customer Brief</div>
                <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 4, lineHeight: 1.5 }}>
                  2-page A4 brief: terms, eligibility, what we ask in return. Print → Save as PDF.
                </div>
                <div style={{ marginTop: 12, fontSize: 12, fontWeight: 700, color: COLORS.magenta, letterSpacing: '0.04em' }}>
                  Open brief →
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* Logo variants */}
        {variants.map(section => (
          <section key={section.section} style={{ marginBottom: 48 }}>
            <h2 style={{
              fontSize: 11, color: COLORS.magenta, letterSpacing: '0.15em',
              textTransform: 'uppercase', fontWeight: 700, marginBottom: 16,
            }}>
              {section.section}
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
              {section.items.map(it => <DownloadCard key={it.file} {...it} />)}
            </div>
          </section>
        ))}

        {/* Footer */}
        <footer style={{
          marginTop: 64,
          padding: '32px 0',
          borderTop: `1px solid ${COLORS.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: COLORS.muted,
          fontSize: 12,
        }}>
          <span>© 2026 CloudVoro · Need EPS, AI, or PDF vector files? Contact <a href="mailto:hello@cloudvoro.com" style={{ color: COLORS.magenta, fontWeight: 600 }}>hello@cloudvoro.com</a></span>
          <Link to="/brochure/sales" style={{ color: COLORS.navy, fontWeight: 600, textDecoration: 'none' }}>View Brochure →</Link>
        </footer>
      </main>
    </div>
  );
}
